import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GetPortfolioOrganizationsAction } from "../../store/action";
import SectionTitle from "../ui/SectionTitle";
import Monogram from "../ui/Monogram";

/**
 * Portfolio Component
 * Organizations with public booking pages.
 *
 * Layout: a swipeable, scroll-snapped slider on mobile; a static grid from `sm`
 * up. The slider is native CSS scroll-snap rather than a carousel library — it
 * keeps momentum scrolling on touch, needs no JS to swipe, and still works if
 * the paging script never runs.
 *
 * @param {string} sectionTitle - Main section title
 * @param {string} sectionDescription - Section description
 */
const Portfolio = ({ sectionTitle, sectionDescription }) => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollerRef = useRef(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        setLoading(true);
        const data = await GetPortfolioOrganizationsAction();
        setOrganizations(data || []);
      } catch (error) {
        console.error("Error fetching portfolio organizations:", error);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, []);

  // Which card is currently snapped: the one whose left edge is nearest the
  // scroll position. Only meaningful on mobile, where the dots are visible.
  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const cards = Array.from(el.children);
    if (!cards.length) return;

    let nearest = 0;
    let smallestDelta = Infinity;
    cards.forEach((card, i) => {
      const delta = Math.abs(card.offsetLeft - el.offsetLeft - el.scrollLeft);
      if (delta < smallestDelta) {
        smallestDelta = delta;
        nearest = i;
      }
    });
    setActiveIndex(nearest);
  }, []);

  const scrollToIndex = useCallback((index) => {
    const el = scrollerRef.current;
    const card = el?.children[index];
    if (!el || !card) return;
    el.scrollTo({
      left: card.offsetLeft - el.offsetLeft,
      behavior: "smooth",
    });
  }, []);

  const canPage = organizations.length > 1;

  return (
    <section id="portfolio" className="portfolio bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionTitle
          title={sectionTitle || t("Portfolio.title")}
          description={sectionDescription || t("Portfolio.description")}
        />

        {/* This section is a taste, not the directory. Anyone who scrolled this
            far is looking for a business, and the search page is where they can
            actually narrow by city, service and day. */}
        <div className="mt-6 text-center">
          <Link
            to="/discover"
            className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-brand transition hover:bg-brand-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            <i className="bx bx-search-alt text-base" aria-hidden="true" />
            {t("Portfolio.findAppointment")}
          </Link>
        </div>

        {loading ? (
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="overflow-hidden rounded-card border border-ink-200"
                aria-hidden="true"
              >
                <div className="aspect-[4/3] animate-pulse bg-ink-100" />
                <div className="space-y-2 p-5">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-ink-100" />
                  <div className="h-3 w-1/3 animate-pulse rounded bg-ink-100" />
                </div>
              </div>
            ))}
            <span className="sr-only">{t("Portfolio.loading")}</span>
          </div>
        ) : organizations.length === 0 ? (
          <div className="mt-14 rounded-card border border-dashed border-ink-300 bg-ink-50 px-6 py-16 text-center">
            <span className="inline-flex size-12 items-center justify-center rounded-full bg-white text-ink-400 shadow-card">
              <i className="bx bx-store text-2xl" aria-hidden="true" />
            </span>
            <p className="mt-4 text-fluid-base text-ink-500">
              {t("Portfolio.noOrganizations")}
            </p>
          </div>
        ) : (
          <>
            {/*
              Mobile: horizontal scroll-snap slider, one whole card per page.
              It used to show a peek of the next card, but a card sliced off
              mid-word at the screen edge read as a broken layout rather than as
              "swipe for more" — the arrows and dots below say that instead.
              From `sm` up the same DOM becomes a grid. The negative margin +
              padding lets a swipe run to the screen edge while keeping the
              resting card aligned with the section gutter.
            */}
            <div
              ref={scrollerRef}
              onScroll={handleScroll}
              /* sm:overflow-x-visible must match the longhand of overflow-x-auto:
                 Tailwind emits the `overflow` shorthand before the longhand, so
                 a `sm:overflow-visible` shorthand loses to it at every width. */
              className="portfolio-container -mx-4 mt-14 flex snap-x snap-mandatory scroll-px-4 gap-6 overflow-x-auto px-4 pb-4 [-ms-overflow-style:none] [scrollbar-width:none] sm:mx-0 sm:grid sm:snap-none sm:grid-cols-2 sm:overflow-x-visible sm:px-0 sm:pb-0 lg:grid-cols-3 [&::-webkit-scrollbar]:hidden"
            >
              {organizations.map((org) => {
                const image = org.headerImageUrl || org.logoUrl;
                return (
                  <article
                    key={org.id}
                    className="portfolio-item group relative w-full shrink-0 snap-start overflow-hidden rounded-card border border-ink-200 bg-white shadow-card transition duration-200 hover:-translate-y-1 hover:shadow-card-hover motion-reduce:transform-none sm:w-auto sm:shrink"
                  >
                    <div className="portfolio-wrap aspect-[4/3] overflow-hidden bg-ink-100">
                      {image ? (
                        <img
                          src={image}
                          alt=""
                          loading="lazy"
                          className="size-full object-cover transition duration-300 group-hover:scale-105 motion-reduce:transform-none"
                        />
                      ) : (
                        <Monogram
                          name={org.businessName}
                          className="size-full transition duration-300 group-hover:scale-105 motion-reduce:transform-none"
                        />
                      )}
                    </div>

                    <div className="portfolio-info p-5">
                      <h4 className="truncate text-base font-semibold text-ink-900">
                        <Link
                          to={`/${org.slug}`}
                          title={t("Portfolio.viewBookingPage")}
                          className="block truncate after:absolute after:inset-0 hover:text-brand-700"
                        >
                          {org.bookingTitle || org.businessName}
                        </Link>
                      </h4>
                      {org.city ? (
                        <p className="mt-1 flex items-center gap-1.5 text-fluid-sm text-ink-500">
                          <i className="bx bx-map text-base" aria-hidden="true" />
                          {org.city}
                        </p>
                      ) : null}
                    </div>

                    <span
                      className="portfolio-links pointer-events-none absolute right-4 top-4 inline-flex size-9 items-center justify-center rounded-full bg-white/95 text-brand-700 opacity-0 shadow-card transition group-hover:opacity-100"
                      aria-hidden="true"
                    >
                      <i className="bx bx-link text-lg" />
                    </span>
                  </article>
                );
              })}
            </div>

            {/* Mobile paging: swipe works on its own, these add an explicit
                control and a position cue. Hidden once the grid takes over. */}
            {/* Only meaningful once there is more than a page's worth to see. */}
            {organizations.length >= 3 && (
              <div className="mt-8 text-center">
                <Link
                  to="/discover"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition hover:text-brand-800 hover:underline"
                >
                  {t("Portfolio.seeAll")}
                  <i className="bi bi-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            )}

            {canPage && (
              <div className="mt-2 flex items-center justify-center gap-1 sm:hidden">
                <button
                  type="button"
                  onClick={() => scrollToIndex(Math.max(activeIndex - 1, 0))}
                  disabled={activeIndex === 0}
                  aria-label={t("Portfolio.previous", "Previous")}
                  className="inline-flex size-11 items-center justify-center rounded-full text-ink-500 transition hover:bg-ink-100 hover:text-brand-700 disabled:pointer-events-none disabled:opacity-30"
                >
                  <i className="bi bi-chevron-left" aria-hidden="true" />
                </button>

                <div className="flex items-center gap-1">
                  {organizations.map((org, i) => (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => scrollToIndex(i)}
                      aria-label={org.bookingTitle || org.businessName}
                      aria-current={i === activeIndex ? "true" : undefined}
                      className="group inline-flex size-8 items-center justify-center"
                    >
                      <span
                        className={`block h-1.5 rounded-full transition-all duration-200 ${
                          i === activeIndex
                            ? "w-5 bg-brand-600"
                            : "w-1.5 bg-ink-300 group-hover:bg-ink-400"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() =>
                    scrollToIndex(
                      Math.min(activeIndex + 1, organizations.length - 1),
                    )
                  }
                  disabled={activeIndex === organizations.length - 1}
                  aria-label={t("Portfolio.next", "Next")}
                  className="inline-flex size-11 items-center justify-center rounded-full text-ink-500 transition hover:bg-ink-100 hover:text-brand-700 disabled:pointer-events-none disabled:opacity-30"
                >
                  <i className="bi bi-chevron-right" aria-hidden="true" />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

Portfolio.propTypes = {
  sectionTitle: PropTypes.string,
  sectionDescription: PropTypes.string,
};

export default Portfolio;
